import type { LpSolve } from '@actual-app/core/shared/mtxbudget-parcours';

import { createSolve, runTask } from './solverTasks';
import type { SolverResult, SolverTask } from './solverTasks';

/**
 * What the solver did this session, for the self-checks: a walk recomputed
 * over and over shows here, and so does a solve slow enough to be felt.
 */
export type SolverStats = {
  tasks: number;
  slowestMs: number;
  inWorker: boolean;
  /** Tasks asked in the last ten seconds: a walk in a loop shows here. */
  recent: number;
  /** How many components asked them: many means components mount anew. */
  origins: number;
};

const stats = { tasks: 0, slowestMs: 0, inWorker: false };
const asked: { at: number; origin: string }[] = [];
const RECENT_MS = 10_000;
// What changed in the inputs from one task to the next of the same kind: a
// loop either rebuilds some field differently each time, or asks again with
// the very same data.
const lastInputs = new Map<string, Record<string, string>>();
let changed: string[] = [];

function noteInputs(task: SolverTask) {
  const fields = Object.fromEntries(
    Object.entries(task.inputs as Record<string, unknown>).map(
      ([key, value]) => [key, JSON.stringify(value) ?? 'undefined'],
    ),
  );
  const previous = lastInputs.get(task.kind);
  if (previous) {
    changed = Object.keys({ ...previous, ...fields }).filter(
      key => previous[key] !== fields[key],
    );
  }
  lastInputs.set(task.kind, fields);
}

export function solverStats(): SolverStats {
  const since = Date.now() - RECENT_MS;
  const recent = asked.filter(entry => entry.at >= since);
  return {
    ...stats,
    recent: recent.length,
    origins: new Set(recent.map(entry => entry.origin)).size,
  };
}

/** The self-check of the solver: off the screen, and never in a loop. */
export function solverCheck(): { name: string; ok: boolean; detail: string } {
  const { tasks, slowestMs, inWorker, recent, origins } = solverStats();
  return {
    name: "Le solveur tourne à part de l'écran",
    ok: inWorker && recent <= 20,
    detail: !inWorker
      ? "calcul sur la page : l'écran attend chaque résolution"
      : recent > 20
        ? `${recent} calculs en 10 s : le parcours se recalcule en boucle (${changed.length > 0 ? `champs qui changent : ${changed.join(', ')}` : 'mêmes données à chaque fois'}, ${origins} composant(s) demandeur(s))`
        : `${tasks} calcul(s), le plus long ${Math.round(slowestMs)} ms`,
  };
}

let worker: Worker | null = null;
let nextId = 0;
const waiting = new Map<
  number,
  { resolve: (value: unknown) => void; reject: (error: Error) => void }
>();
let onPage: Promise<LpSolve> | null = null;

function startWorker(): Worker | null {
  if (worker || typeof Worker === 'undefined') {
    return worker;
  }
  try {
    worker = new Worker(new URL('./solver.worker.ts', import.meta.url), {
      type: 'module',
    });
  } catch {
    return null;
  }
  worker.onmessage = (
    event: MessageEvent<{
      id: number;
      result?: unknown;
      error?: string;
      ms?: number;
    }>,
  ) => {
    const { id, result, error, ms = 0 } = event.data;
    const pending = waiting.get(id);
    waiting.delete(id);
    stats.slowestMs = Math.max(stats.slowestMs, ms);
    if (error !== undefined) {
      pending?.reject(new Error(error));
    } else {
      pending?.resolve(result);
    }
  };
  stats.inWorker = true;
  return worker;
}

/**
 * Asks the linear-programming solver (HiGHS, WebAssembly) one thing. It runs
 * in its own Worker, so the screen answers while it works; on the page only
 * when no Worker can start.
 */
export function solveTask<T extends SolverTask>(
  task: T,
  origin = 'inconnu',
): Promise<SolverResult<T>> {
  stats.tasks += 1;
  noteInputs(task);
  asked.push({ at: Date.now(), origin });
  if (asked.length > 200) {
    asked.splice(0, asked.length - 200);
  }
  const thread = startWorker();
  if (thread) {
    const id = (nextId += 1);
    return new Promise((resolve, reject) => {
      waiting.set(id, {
        resolve: value => resolve(value as SolverResult<T>),
        reject,
      });
      thread.postMessage({ id, task });
    });
  }
  onPage ??= createSolve();
  return onPage.then(solve => {
    const started = performance.now();
    const result = runTask(task, solve) as SolverResult<T>;
    stats.slowestMs = Math.max(stats.slowestMs, performance.now() - started);
    return result;
  });
}
